import { useCallback, useMemo } from 'react'
import { AttachmentsList } from 'components/AttachmentsList/AttachmentsList'
import { useContractDetailsContext } from 'features/ContractDetails/Providers/ContractDetailsContextProvider'
import { BTN_TXT } from 'constants/txt'
import { DOCUMENT_TYPE } from 'components/FileUpload/constants'
import { validateFileTypes } from 'helper/common'
import { FileUpload } from 'components/FileUpload/FileUpload'
import { TDocumentTypeUnion } from 'components/FileUpload/types'
import { getAcceptFileTypesByDocumentType } from 'components/FileUpload/utils'
import { ATTACHMENTS_FILE_TYPES } from 'features/ContractDetails/constants'
import { ReactComponent as Docs16 } from 'assets/svg/Docs16.svg'
import { formatAmendmentDocument } from './utils'
import { Button, BUTTON_TYPES } from '../../../../components/Button'
import { useSelector } from 'react-redux'
import { getUser } from '../../../../redux/store/user/getters'
import { AddSignatoryPopup } from './AddSignatoryPopup/AddSignatoryPopup'
import { MAX_FILE_SIZE_25_MB, ROLES_NAMES } from '../../../../constants'
import { useContractDetailsPopup } from '../../Providers/ContractDetailsPopupProvider'
import AcceptContractDocumentPopup from '../ContractDetailsSignUsers/AcceptContractDocument/AcceptContractDocumentPopup'
import './styles.scss'
import { UploadMultipleContractDocumentsPopup } from './UploadMultipleContractDocumentsPopup/UploadMultipleContractDocumentsPopup'
import { Banner } from 'components/Banner/Banner'
import { AddContractButton } from './AddContractButton'
import {
  CONTRACT_DOCUMENTS_LIMIT_TOOLTIP,
  MAX_CONTRACT_DOCUMENTS
} from './constants'
import { TContractDocument } from 'features/ContractDetails/types'
import { DOCUMENT_REQUIRED_ACTION } from 'components/Status/constants'

const ContractDetailsDocuments = () => {
  const user = useSelector(getUser)
  const contractDetailsContext = useContractDetailsContext()
  const { acceptContractDocumentPopup, uploadAutoContractDocumentPopup } =
    useContractDetailsPopup()

  const {
    details,
    contractDocuments,
    canEditContract,
    isOnboarded,
    isAutoGeneratedContract,
    canAddSignatory,
    canViewSignatory,
    canViewRedliningBanners,
    canUploadAttachments,
    canUploadInitialDocument,
    canDeleteNewDocument,
    canUploadNewDocument,
    canUploadNewDocumentVersion,
    canDownloadTemplates,
    canAcceptContract,
    canDownloadDocuments,
    hasContractDocsArray,
    hasContractAttachmentsArray,
    hasContractTemplatesArray,
    canDownloadAttachments
  } = contractDetailsContext.state
  const {
    handleUploadAttachmentsAsync,
    handleUploadContractDocumentAsync,
    handleDeleteAttachmentsAsync,
    handleDeleteContractDocumentAsync
  } = contractDetailsContext.actions

  const hasSignatoryPermissions = canAddSignatory || canViewSignatory
  const hasUploadPermissions =
    canUploadInitialDocument ||
    canUploadNewDocument ||
    (canUploadNewDocumentVersion && hasContractDocsArray)

  const amendmentDocument = useMemo(
    () =>
      details?.amendment_document
        ? [formatAmendmentDocument(details.amendment_document)]
        : [],
    [details.amendment_document]
  )

  const hasAmendmentDocument = !!amendmentDocument.length && isOnboarded

  const isRedliningButtonsAvailable =
    isAutoGeneratedContract && hasContractDocsArray

  const isTemplateSectionAvailable =
    hasContractTemplatesArray &&
    !isOnboarded &&
    !isRedliningButtonsAvailable &&
    canDownloadTemplates

  const isAttachmentsSectionAvailable =
    canEditContract ||
    canUploadAttachments ||
    (canDownloadAttachments && hasContractAttachmentsArray)

  const isAcceptButtonAvailable =
    isRedliningButtonsAvailable && canAcceptContract

  const contractAgreementSectionAvailable =
    hasSignatoryPermissions || canDownloadDocuments || hasUploadPermissions

  const isContractDocumentsLimitReached =
    (contractDocuments?.length ?? 0) >= MAX_CONTRACT_DOCUMENTS

  const renderUploadFileButton = useCallback(
    (
      documentType: TDocumentTypeUnion,
      viewType: 'attachments' | 'contractCreate' | 'contractUpdate',
      documentId?: string
    ) => {
      const acceptFileTypes =
        viewType !== 'attachments'
          ? ATTACHMENTS_FILE_TYPES
          : getAcceptFileTypesByDocumentType(documentType)

      const handleUploadFile =
        viewType !== 'attachments'
          ? handleUploadContractDocumentAsync
          : handleUploadAttachmentsAsync

      const handleDropAcceptedFile = async (file) => {
        await handleUploadFile(file, documentType, documentId)
      }

      const validateFile = (file) =>
        validateFileTypes(acceptFileTypes, file, MAX_FILE_SIZE_25_MB)

      const targetDocument = documentId
        ? contractDocuments?.find((doc) => doc.uuid === documentId)
        : contractDocuments?.[0]

      // on signing step we don't need to show upload button
      if (
        viewType === 'contractUpdate' &&
        (targetDocument?.required_action ===
          DOCUMENT_REQUIRED_ACTION.AWAITING_SIGNATURE ||
          targetDocument?.required_action ===
            DOCUMENT_REQUIRED_ACTION.PENDING_SIGNATORY)
      ) {
        return null
      }

      // specific view for redlining flow
      let redliningButtonProps = {}
      if (
        isRedliningButtonsAvailable &&
        documentType === DOCUMENT_TYPE.TEMPLATE &&
        targetDocument?.is_redlining_required
      ) {
        redliningButtonProps = {
          uploadButtonText: BTN_TXT.UPLOAD_REDLINES,
          icon: <></>,
          small: true,
          btnType: BUTTON_TYPES.DEFAULT
        }
      }

      // specific view for update flow (located in the Attachments view)
      let updateBtnProps = {}
      if (viewType === 'contractUpdate') {
        updateBtnProps = {
          uploadButtonText: BTN_TXT.UPLOAD_NEW_VERSION,
          icon: <></>,
          medium: true,
          btnType: BUTTON_TYPES.DEFAULT
        }
      }

      let canUpload = false

      switch (viewType) {
        case 'attachments':
          canUpload =
            canUploadAttachments && documentType === DOCUMENT_TYPE.ATTACHMENTS
          break
        case 'contractCreate':
          // for auto contracts user will upload new version
          // however for manual contracts user will upload new document
          if (isRedliningButtonsAvailable) {
            canUpload = canUploadNewDocumentVersion
          } else {
            canUpload = hasContractDocsArray
              ? canUploadNewDocument
              : canUploadInitialDocument
          }
          break
        case 'contractUpdate':
          canUpload = canUploadNewDocumentVersion
          break
      }

      const isUploadLimitReached =
        viewType === 'contractCreate' && isContractDocumentsLimitReached

      // if auto contract and create view and can upload, show add contract button instead of file upload
      if (
        canUpload &&
        isAutoGeneratedContract &&
        viewType === 'contractCreate'
      ) {
        return (
          <AddContractButton
            onClick={() =>
              uploadAutoContractDocumentPopup.actions.open({
                uploadedDocumentsCount: contractDocuments?.length ?? 0
              })
            }
            disabled={isUploadLimitReached}
          />
        )
      }

      return canUpload ? (
        <FileUpload
          documentType={documentType}
          uploadBtnText={
            isRedliningButtonsAvailable || viewType === 'attachments'
              ? BTN_TXT.UPLOAD
              : BTN_TXT.ADD_CONTRACT
          }
          handleUploadFile={(files, docType) =>
            handleUploadFile(files, docType, documentId)
          }
          disabled={isUploadLimitReached}
          tooltipText={
            isUploadLimitReached ? CONTRACT_DOCUMENTS_LIMIT_TOOLTIP : undefined
          }
          {...updateBtnProps}
          {...redliningButtonProps}
          uploadFilesProps={{
            multiple: false,
            maxSize: MAX_FILE_SIZE_25_MB,
            onDropAccepted: handleDropAcceptedFile,
            accept: acceptFileTypes,
            validator: validateFile
          }}
        />
      ) : null
    },
    [
      handleUploadContractDocumentAsync,
      handleUploadAttachmentsAsync,
      isRedliningButtonsAvailable,
      contractDocuments,
      isAutoGeneratedContract,
      canUploadAttachments,
      canUploadNewDocumentVersion,
      hasContractDocsArray,
      canUploadNewDocument,
      canUploadInitialDocument,
      uploadAutoContractDocumentPopup.actions,
      isContractDocumentsLimitReached
    ]
  )

  const isInAcceptanceStep = useCallback(
    (document: TContractDocument | undefined) => {
      if (!document) return false
      const isInAcceptanceStep =
        document?.required_action ===
        DOCUMENT_REQUIRED_ACTION.AWAITING_ACCEPTANCE
      const isAcceptedUserAvailable = document?.accepted_by.every(
        (acceptedUser) => !acceptedUser.role.includes(user.role.split('_')[0])
      )
      return isInAcceptanceStep && isAcceptedUserAvailable
    },
    [user.role]
  )

  const renderBannerComponent = useCallback(
    (docId: string) => {
      const document = contractDocuments?.find((doc) => doc.uuid === docId)
      const isAcceptVisible = isInAcceptanceStep(document)
      if (
        !isAutoGeneratedContract ||
        !canViewRedliningBanners ||
        document?.is_accepted_automatically
      ) {
        return undefined
      }
      if (isAcceptVisible) {
        return (
          <Banner.InfoWithTitle
            className="mb-8"
            title="A new version of the contract is now available"
            text="Please review and either accept the contract  or provide a new version with your revisions."
          />
        )
      } else if (hasContractDocsArray) {
        const acceptedInstanceUser = document?.accepted_by.find(
          (acceptedUser) => acceptedUser.role.includes(user.role.split('_')[0])
        )
        if (acceptedInstanceUser) {
          return (
            <Banner.Warning
              className="mb-8"
              text={`The contract has been accepted by ${
                ROLES_NAMES[acceptedInstanceUser.role]
              }.`}
            />
          )
        }
      }
      return undefined
    },
    [
      contractDocuments,
      isInAcceptanceStep,
      hasContractDocsArray,
      canViewRedliningBanners,
      isAutoGeneratedContract,
      user.role
    ]
  )

  const renderActionButtonComponent = useCallback(
    (docId: string) => {
      const document = contractDocuments?.find((doc) => doc.uuid === docId)
      return (
        <div className="column gap-8">
          {renderUploadFileButton(
            DOCUMENT_TYPE.TEMPLATE,
            'contractUpdate',
            docId
          )}
          {isAcceptButtonAvailable && isInAcceptanceStep(document) && (
            <Button
              type={BUTTON_TYPES.DEFAULT}
              className="mr-8"
              onClick={() =>
                acceptContractDocumentPopup.actions.open({ documentId: docId })
              }
              medium
              upperCase
            >
              {BTN_TXT.ACCEPT_CONTRACT}
            </Button>
          )}
        </div>
      )
    },
    [
      isAcceptButtonAvailable,
      acceptContractDocumentPopup.actions,
      isInAcceptanceStep,
      renderUploadFileButton,
      contractDocuments
    ]
  )

  const templateSection = useMemo(() => {
    if (!isTemplateSectionAvailable) return null
    return (
      <AttachmentsList
        label="Templates"
        files={details.contract_document_templates.filter(
          (d) => d.document_type === 'contract_document'
        )}
        disableActions
      />
    )
  }, [isTemplateSectionAvailable, details.contract_document_templates])

  const contractAgreementSection = useMemo(() => {
    if (!contractAgreementSectionAvailable) {
      return null
    }

    const infoTooltipText = isAutoGeneratedContract
      ? 'Please download the contract, review it, add your redlines, and upload the updated file with your changes.'
      : 'Master agreement. Visible only to the contracting team and vendor.'

    const deleteButton = canDeleteNewDocument
      ? {
          enabled: !!contractDocuments && contractDocuments.length > 1,
          onClick: handleDeleteContractDocumentAsync
        }
      : undefined

    const hasSignatoriesSection = (file: TContractDocument) =>
      isAutoGeneratedContract &&
      hasSignatoryPermissions &&
      (!!file.is_redlining_required || !!file.is_digital_signature_required)

    return (
      <AttachmentsList.WithExpand
        label="Contract"
        infoTooltipText={infoTooltipText}
        expandedFiles={contractDocuments || undefined}
        renderActionButtonComponent={renderActionButtonComponent}
        renderBannerComponent={
          isAutoGeneratedContract ? renderBannerComponent : undefined
        }
        deleteButton={deleteButton}
        hasSignatoriesSection={hasSignatoriesSection}
      />
    )
  }, [
    canDeleteNewDocument,
    contractAgreementSectionAvailable,
    contractDocuments,
    handleDeleteContractDocumentAsync,
    hasSignatoryPermissions,
    isAutoGeneratedContract,
    renderActionButtonComponent,
    renderBannerComponent
  ])

  const contractAmendmentSection = useMemo(() => {
    if (!hasAmendmentDocument) return null
    return (
      <AttachmentsList
        label="LOP"
        files={amendmentDocument}
        icon={<Docs16 />}
        disableActions
      />
    )
  }, [hasAmendmentDocument, amendmentDocument])

  return (
    <>
      <div className="contact-details-documents column gap-24">
        {templateSection}
        <div className="contact-details-documents__row column">
          {contractAgreementSection}
          {contractAmendmentSection}
          <div className="contact-details-documents__row">
            {renderUploadFileButton(
              DOCUMENT_TYPE.TEMPLATE,
              'contractCreate',
              // if single mode not enabled and doc already created - update it instead of adding one more -- for manual contracts
              contractDocuments?.length && isRedliningButtonsAvailable
                ? contractDocuments?.[0]?.uuid
                : undefined
            )}
          </div>
        </div>
        {isAttachmentsSectionAvailable && (
          <div className="contact-details-documents__row column">
            <AttachmentsList
              label="Attachments"
              files={details.contract_attachments}
              onDelete={handleDeleteAttachmentsAsync}
              disableActions={!canEditContract}
              infoTooltipText="Visible to all hospital participants."
            />
            {details.contract_attachments.length < 10 &&
              canUploadAttachments &&
              renderUploadFileButton(DOCUMENT_TYPE.ATTACHMENTS, 'attachments')}
          </div>
        )}
      </div>
      <AcceptContractDocumentPopup />
      <AddSignatoryPopup />
      <UploadMultipleContractDocumentsPopup />
    </>
  )
}

export default ContractDetailsDocuments
